// Rich Tooltip: a tooltip that holds real content — a heading, a paragraph, a link —
// instead of a string. <i80-tooltip> is the one for a string; this one is a popover with
// a toggle, anchored with Floating UI, exactly as the Ember component does it.
import { computePosition, autoUpdate, offset, flip, shift, limitShift, autoPlacement, arrow } from "@floating-ui/dom";
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

const PLACEMENTS = ["top", "top-start", "top-end", "right", "right-start", "right-end",
  "bottom", "bottom-start", "bottom-end", "left", "left-start", "left-end"];
const COLLISION = ["true", "false", "flip", "shift", "auto"];
const SIZES = ["small", "medium", "large"];
const EDGE = 8;

/** Our own part inside `parent`, never one belonging to a nested <i80-*> child. */
const own = (parent, name) => parent.querySelector(`:scope > [data-i80-part="${name}"]`);

/**
 * <i80-rich-tooltip text="More info" icon="info">
 *   <h4 class="i80-text i80-typography-display-200 i80-font-weight-semibold">Who can see this?</h4>
 *   <p class="i80-text i80-typography-body-200 i80-font-weight-regular">Everyone on the team.</p>
 * </i80-rich-tooltip>
 *
 * The toggle is text with a dotted underline and an optional icon; everything else you
 * write inside is the bubble's content. A child with slot="toggle" becomes the toggle
 * instead of the text (Helios' "generic toggle"). Hover and focus open it; trigger="click"
 * asks for a click instead. Esc and a click outside close it — it is a native popover.
 */
export class I80RichTooltip extends I80Element {
  static get observedAttributes() {
    return ["text", "icon", "icon-position", "size", "inline", "placement", "offset",
      "collision-detection", "width", "height", "trigger", "open"];
  }

  setup() {
    let root = this.part("rich-tooltip");
    if (!root) {
      root = h("div", { class: "i80-rich-tooltip", "data-i80-part": "rich-tooltip" },
        h("button", { type: "button", "data-i80-part": "toggle" }),
        h("div", { class: "i80-rich-tooltip__bubble", tabindex: "-1", role: "tooltip", popover: "auto", "data-i80-part": "bubble" },
          h("div", { class: "i80-rich-tooltip__bubble-arrow", "data-i80-part": "arrow" }),
          h("div", { class: "i80-rich-tooltip__bubble-inner-content", "data-i80-part": "inner" })));
      this.append(root);
      // The content is usually whole components (a Text, a Link), and when a tree built
      // off-document is inserted they exist but have not built themselves yet. Take them
      // in a microtask later, the same route children arriving late take.
      queueMicrotask(() => {
        if (!this.hasStrayContent()) return;
        this.adopt();
        this.update();
      });
    }
    this._root = root;
    this._toggle = own(root, "toggle");
    this._bubble = own(root, "bubble");
    this._arrow = own(this._bubble, "arrow");
    this._inner = own(this._bubble, "inner");
    // New ids on every instance, so a clone's toggle never points at the original's bubble.
    this._bubble.id = nextId("i80-rich-tooltip");
    this._arrow.id = nextId("i80-rich-tooltip-arrow");
    this._toggle.setAttribute("aria-describedby", this._bubble.id);
    this._toggle.setAttribute("aria-controls", this._bubble.id);

    this._bubble.addEventListener("toggle", (e) => this._onToggle(e.newState === "open"));
    this.addEventListener("pointerenter", () => this._soft() && this.show(), true);
    this.addEventListener("pointerleave", () => this._soft() && this._hideSoon(), true);
    this.addEventListener("focusin", () => this._soft() && this.show());
    this.addEventListener("focusout", () => this._soft() && this._hideSoon());
  }

  adopt() {
    for (const node of this.takeContent()) {
      if (node.nodeType === Node.ELEMENT_NODE && node.getAttribute("slot") === "toggle") {
        node.removeAttribute("slot");
        this._toggle.setAttribute("data-i80-custom", "");
        this._toggle.append(node);
      } else {
        this._inner.append(node);
      }
    }
  }

  /** Hover and focus open it unless a click was asked for. */
  _soft() {
    return this.choice("trigger", ["hover", "click"], "hover") === "hover";
  }

  get open() {
    return this._bubble.matches(":popover-open");
  }

  set open(value) {
    if (value) this.show();
    else this.hide();
  }

  show() {
    clearTimeout(this._hideTimer);
    if (!this.open) this._bubble.showPopover();
    this._reflect(true);
  }

  hide() {
    clearTimeout(this._hideTimer);
    if (this.open) this._bubble.hidePopover();
    this._reflect(false);
  }

  /**
   * open says what is on screen. The popover's toggle event arrives a task later, so
   * show() and hide() write it at once — otherwise update() would run in between, see the
   * attribute disagreeing with the popover, and close what was just opened.
   */
  _reflect(open) {
    if (this.flag("open") !== open) this.toggleAttribute("open", open);
  }

  // A short grace period lets the pointer travel from the toggle onto the bubble.
  _hideSoon() {
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => {
      if (!this.matches(":hover") && !this.contains(document.activeElement)) this.hide();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopPositioning?.();
    this._stopPositioning = null;
  }

  update() {
    const text = this.getAttribute("text");
    const inline = this.flag("inline");
    const name = this.getAttribute("icon");
    // Helios gives the toggle a typographic style only when it has text of its own.
    const size = text ? this.getAttribute("size") && this.choice("size", SIZES, "") : "";

    this._toggle.className = [
      "i80-rich-tooltip__toggle",
      inline ? "i80-rich-tooltip__toggle--is-inline" : "i80-rich-tooltip__toggle--is-block",
      size && `i80-rich-tooltip__toggle--size-${size}`,
    ].filter(Boolean).join(" ");
    this._toggle.setAttribute("aria-expanded", String(this.open));

    if (!this._toggle.hasAttribute("data-i80-custom")) {
      const iconEl = name ? icon(name, { className: `${inline ? "i80-icon--is-inline " : ""}i80-rich-tooltip__toggle-icon` }) : null;
      const label = text ? h("span", { class: "i80-rich-tooltip__toggle-text" }, text) : null;
      const trailing = this.choice("icon-position", ["leading", "trailing"], "trailing") === "trailing";
      this._toggle.replaceChildren(...(trailing ? [label, iconEl] : [iconEl, label]).filter(Boolean));
    }

    // Only a click toggle is wired to the popover by the browser; hover and focus are ours.
    if (this._soft()) this._toggle.removeAttribute("popovertarget");
    else this._toggle.setAttribute("popovertarget", this._bubble.id);

    const width = this.getAttribute("width");
    const height = this.getAttribute("height");
    Object.assign(this._bubble.style, {
      width: width ?? "",
      maxWidth: width ? "none" : "",
      height: height ?? "",
      maxHeight: height ? "none" : "",
    });
    if (this.open) this._bubble.removeAttribute("aria-hidden");
    else this._bubble.setAttribute("aria-hidden", "true");

    // open is a control and a report: the popover's own toggle event writes it back.
    if (this.flag("open") !== this.open) {
      queueMicrotask(() => {
        if (this.isConnected && this.flag("open") !== this.open) this.open = this.flag("open");
      });
    }
  }

  /** The Floating UI middleware for the placement and collision options given. */
  _middleware() {
    const collision = this.choice("collision-detection", COLLISION, "true");
    const distance = Number(this.getAttribute("offset") ?? 12);
    const list = [offset(Number.isFinite(distance) ? distance : 12)];
    if (collision === "true" || collision === "flip") list.push(flip({ padding: EDGE }));
    if (collision === "true" || collision === "shift") list.push(shift({ padding: EDGE, limiter: limitShift() }));
    if (collision === "auto") list.push(autoPlacement({ padding: EDGE }));
    list.push(arrow({ element: this._arrow, padding: 12 }));
    return list;
  }

  _onToggle(open) {
    this._toggle.setAttribute("aria-expanded", String(open));
    if (open) this._bubble.removeAttribute("aria-hidden");
    else this._bubble.setAttribute("aria-hidden", "true");
    this._reflect(open);
    this._stopPositioning?.();
    this._stopPositioning = null;
    if (open) {
      const placement = this.choice("placement", PLACEMENTS, "bottom");
      this._stopPositioning = autoUpdate(this._toggle, this._bubble, () =>
        computePosition(this._toggle, this._bubble, { placement, strategy: "absolute", middleware: this._middleware() })
          .then(({ x, y, placement: final, middlewareData }) => {
            Object.assign(this._bubble.style, { position: "absolute", left: `${x}px`, top: `${y}px` });
            // The arrow is a square background image rotated by CSS per side, as in Helios.
            this._arrow.setAttribute("data-i80-anchored-arrow-placement", final);
            const a = middlewareData.arrow ?? {};
            Object.assign(this._arrow.style, {
              left: a.x != null ? `${a.x}px` : "",
              top: a.y != null ? `${a.y}px` : "",
            });
          }));
    }
    this.dispatchEvent(new CustomEvent(open ? "i80-open" : "i80-close", { bubbles: true }));
  }
}

define("i80-rich-tooltip", I80RichTooltip);
